/**
 * Room tone — the quiet bed under each location.
 *
 * This is deliberately *not* music. A looping tune under a three-minute timed
 * quiz is exhausting and fights the narrator; a barely-there bed does the one
 * job that matters, which is stopping the game from feeling like a silent
 * PowerPoint between cues. Target level is around -40dBFS: if a player notices
 * it as a sound rather than as a place, it is too loud.
 *
 * Each bed is the same three ingredients, retuned per location:
 *   1. two brown-noise loops, panned hard-ish L/R and detuned against each other
 *      (decorrelating them is what turns "hiss in your head" into "air in a
 *      room", and it also hides the loop point),
 *   2. a slow LFO breathing the lowpass cutoff so the bed never sits still,
 *   3. a very quiet consonant drone, so the bed shares a key with the cues.
 *
 * Switching location crossfades over 1.6s rather than cutting, because a hard
 * change of noise floor is one of the most noticeable edits in all of audio.
 */
import { clamp, getRig, isRunning } from './context';

export type AmbienceKind = 'home' | 'factory' | 'hub';

interface Preset {
  /** Lowpass cutoff of the noise bed, Hz. Lower = further away, warmer. */
  cutoff: number;
  /** How far the slow LFO breathes that cutoff, Hz. */
  breath: number;
  /** LFO rate, Hz. All well under 0.2 — this must never read as a wobble. */
  breathRate: number;
  bed: number;
  /** Consonant drone, in Hz. Kept in C/G so it agrees with the cue set. */
  drone: readonly number[];
  droneGain: number;
  droneCutoff: number;
  /** Optional slow amplitude pulse — distant machinery, for the factory only. */
  pulseRate?: number;
  pulseDepth?: number;
}

const PRESETS: Record<AmbienceKind, Preset> = {
  // A living room: soft, close, mostly low. A fifth (A2+E3) reads as "settled".
  home: {
    cutoff: 380,
    breath: 90,
    breathRate: 0.055,
    bed: 0.1,
    drone: [110, 164.81],
    droneGain: 0.02,
    droneCutoff: 620,
    },
  // A factory: brighter and wider, with a 0.75Hz swell so something feels like
  // it is running somewhere out of shot. Still soft — this is a cartoon plant.
  factory: {
    cutoff: 620,
    breath: 160,
    breathRate: 0.085,
    bed: 0.105,
    drone: [98, 146.83],
    droneGain: 0.022,
    droneCutoff: 520,
    pulseRate: 0.75,
    pulseDepth: 0.35,
  },
  // The hub: airy and open, on a plain C major triad — the storybook cover.
  hub: {
    cutoff: 900,
    breath: 220,
    breathRate: 0.045,
    bed: 0.075,
    drone: [130.81, 164.81, 196],
    droneGain: 0.016,
    droneCutoff: 900,
  },
};

const FADE_IN = 1.6;
const FADE_OUT = 1.6;

interface Layer {
  kind: AmbienceKind;
  gain: GainNode;
  sources: AudioScheduledSourceNode[];
  nodes: AudioNode[];
}

let current: Layer | null = null;
let desired: AmbienceKind | null = null;
const retireTimers = new Set<number>();

/* ------------------------------------------------------------------ *
 * Build / tear down one bed
 * ------------------------------------------------------------------ */

function build(kind: AmbienceKind): Layer | null {
  const r = getRig();
  if (!r) return null;
  const ctx = r.ctx;
  const p = PRESETS[kind];
  const t = ctx.currentTime;

  const sources: AudioScheduledSourceNode[] = [];
  const nodes: AudioNode[] = [];

  const out = ctx.createGain();
  out.gain.setValueAtTime(0.0001, t);
  out.gain.linearRampToValueAtTime(1, t + FADE_IN);
  out.connect(r.ambience);
  nodes.push(out);

  /* --- noise bed ---------------------------------------------------- */
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(p.cutoff, t);
  lp.Q.setValueAtTime(0.6, t);
  nodes.push(lp);

  const bedGain = ctx.createGain();
  bedGain.gain.setValueAtTime(p.bed, t);
  lp.connect(bedGain);
  bedGain.connect(out);
  nodes.push(bedGain);

  for (const side of [-0.65, 0.65]) {
    const src = ctx.createBufferSource();
    src.buffer = r.brown;
    src.loop = true;
    // Detuning the two copies against each other is what decorrelates them.
    src.playbackRate.setValueAtTime(side < 0 ? 0.97 : 1.03, t);
    if (r.canPan) {
      const pan = ctx.createStereoPanner();
      pan.pan.setValueAtTime(side, t);
      src.connect(pan);
      pan.connect(lp);
      nodes.push(pan);
    } else {
      src.connect(lp);
    }
    src.start(t, Math.random() * Math.max(0.01, r.brown.duration - 0.2));
    sources.push(src);
  }

  /* --- slow breath on the cutoff ------------------------------------ */
  const breathLfo = ctx.createOscillator();
  breathLfo.type = 'sine';
  breathLfo.frequency.setValueAtTime(p.breathRate, t);
  const breathDepth = ctx.createGain();
  breathDepth.gain.setValueAtTime(p.breath, t);
  breathLfo.connect(breathDepth);
  breathDepth.connect(lp.frequency);
  breathLfo.start(t);
  sources.push(breathLfo);
  nodes.push(breathDepth);

  /* --- optional machinery pulse ------------------------------------- */
  if (p.pulseRate && p.pulseDepth) {
    const pulse = ctx.createOscillator();
    pulse.type = 'sine';
    pulse.frequency.setValueAtTime(p.pulseRate, t);
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(p.bed * p.pulseDepth, t);
    pulse.connect(depth);
    depth.connect(bedGain.gain);
    pulse.start(t);
    sources.push(pulse);
    nodes.push(depth);
  }

  /* --- drone -------------------------------------------------------- */
  const droneLp = ctx.createBiquadFilter();
  droneLp.type = 'lowpass';
  droneLp.frequency.setValueAtTime(p.droneCutoff, t);
  droneLp.Q.setValueAtTime(0.5, t);
  const droneGain = ctx.createGain();
  droneGain.gain.setValueAtTime(p.droneGain, t);
  droneLp.connect(droneGain);
  droneGain.connect(out);
  // A touch of the shared plate glues the drone to the cues that sit on top.
  const send = ctx.createGain();
  send.gain.setValueAtTime(0.25, t);
  droneGain.connect(send);
  send.connect(r.reverb);
  nodes.push(droneLp, droneGain, send);

  let spread = -0.3;
  for (const f of p.drone) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f, t);
    // A few cents of detune per voice gives a slow natural chorus for free.
    osc.detune.setValueAtTime(spread * 18, t);
    if (r.canPan) {
      const pan = ctx.createStereoPanner();
      pan.pan.setValueAtTime(clamp(spread, -1, 1), t);
      osc.connect(pan);
      pan.connect(droneLp);
      nodes.push(pan);
    } else {
      osc.connect(droneLp);
    }
    osc.start(t);
    sources.push(osc);
    spread += 0.3;
  }

  return { kind, gain: out, sources, nodes };
}

function fadeOutAndRetire(layer: Layer, seconds: number): void {
  const r = getRig();
  if (!r) {
    hardStop(layer);
    return;
  }
  const t = r.ctx.currentTime;
  try {
    layer.gain.gain.cancelScheduledValues(t);
    layer.gain.gain.setValueAtTime(Math.max(0.0001, layer.gain.gain.value), t);
    layer.gain.gain.linearRampToValueAtTime(0.0001, t + seconds);
    for (const s of layer.sources) s.stop(t + seconds + 0.05);
  } catch {
    hardStop(layer);
    return;
  }
  const timer = setTimeout(() => {
    retireTimers.delete(timer);
    hardStop(layer);
  }, (seconds + 0.2) * 1000) as unknown as number;
  retireTimers.add(timer);
}

function hardStop(layer: Layer): void {
  for (const s of layer.sources) {
    try {
      s.stop();
    } catch {
      /* Already stopped, or the context is gone. */
    }
    s.disconnect();
  }
  for (const n of layer.nodes) n.disconnect();
  layer.sources.length = 0;
  layer.nodes.length = 0;
}

/* ------------------------------------------------------------------ *
 * Public (module-internal) API
 * ------------------------------------------------------------------ */

/**
 * Idempotent: asking for the bed that is already playing does nothing at all,
 * so a scene can call this on every `enter()` without stuttering the room tone.
 * Asking before the context is unlocked just records the intent; the bed starts
 * itself the moment audio becomes available.
 */
export function startAmbienceInternal(kind: AmbienceKind): void {
  desired = kind;
  if (!isRunning()) return;
  if (current && current.kind === kind) return;
  if (current) fadeOutAndRetire(current, FADE_OUT);
  current = build(kind);
}

export function stopAmbienceInternal(): void {
  desired = null;
  if (current) fadeOutAndRetire(current, FADE_OUT);
  current = null;
}

/** Called when the context finally reaches `running`. */
export function applyPendingAmbience(): void {
  if (desired && isRunning() && (!current || current.kind !== desired)) {
    startAmbienceInternal(desired);
  }
}

export function currentAmbience(): AmbienceKind | null {
  return current ? current.kind : null;
}

export function disposeAmbience(): void {
  for (const t of retireTimers) clearTimeout(t);
  retireTimers.clear();
  if (current) hardStop(current);
  current = null;
  desired = null;
}
